import { useState, useEffect } from 'react';
import { stripMarkup, speechLang } from '../../utils/narration';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../utils/api-url';
import { useIsMobile } from '../../hooks/useIsMobile';


interface GameIntroScreenProps {
  gameId: string;
  onContinue?: () => void;
}

export default function GameIntroScreen({ gameId, onContinue }: GameIntroScreenProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [intro, setIntro] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const apiUrl = getApiUrl();

  // Web Speech API for narration
  const speakNarrative = (text: string) => {
    if ('speechSynthesis' in window) {
      // Stop any ongoing speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      // Was hardcoded ru-RU while the interface ships in eight languages, so a player on
      // any other locale had their text read by a Russian voice.
      utterance.lang = speechLang(i18n.language);
      utterance.rate = 0.9; // Slightly slower for dramatic effect
      utterance.pitch = 0.9; // Slightly lower pitch for narrator voice
      utterance.volume = 1.0;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      window.speechSynthesis.speak(utterance);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | undefined;
    let skipTimeout: ReturnType<typeof setTimeout> | undefined;

    const stopPolling = () => {
      if (interval) clearInterval(interval);
      if (skipTimeout) clearTimeout(skipTimeout);
    };

    const fetchIntro = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/games/${gameId}/intro`);

        if (!response.ok) {
          if (response.status === 404) {
            // Intro not ready yet — keep polling
            return;
          }
          throw new Error('Failed to fetch intro');
        }

        const data = await response.json();
        if (cancelled) return;
        setIntro(data); // set once; effect does NOT depend on `intro`
        setIsLoading(false);
        stopPolling(); // loaded — stop polling so audio is not restarted every 2s
      } catch (err: any) {
        if (cancelled) return;
        console.error('Error fetching intro:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchIntro();

    // Poll every 2 seconds until the intro is ready
    interval = setInterval(fetchIntro, 2000);

    // Give up after 10 seconds (old games created before the intro feature)
    skipTimeout = setTimeout(() => {
      if (cancelled) return;
      setIsLoading(false);
      setError(t('intro.notAvailable'));
      stopPolling();
    }, 10000);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [gameId, apiUrl]);

  // Auto-start narration when intro loads (only for Web Speech, not ElevenLabs audio)
  useEffect(() => {
    if (intro && intro.narrative && intro.useWebSpeech && !intro.audioId) {
      // stripMarkup, not a local cleaner: the previous one removed markdown but left square
      // brackets untouched, so the browser voice read "[laughs]" out loud as words -- on the exact
      // path that runs when server-side narration is unavailable, i.e. for the languages with no
      // offline engine.
      speakNarrative(stripMarkup(intro.narrative));
    }

    // Cleanup on unmount
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, [intro]);

  if (isLoading && !intro) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        color: '#e2e8f0',
        padding: isMobile ? '1.25rem' : '2rem',
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '2rem', animation: 'pulse 2s infinite' }}>
          🎬
        </div>
        <h2 style={{ fontSize: isMobile ? '1.5rem' : '2rem', marginBottom: '1rem', textAlign: 'center' }}>
          {t('intro.creating')}
        </h2>
        <p style={{ color: '#a0aec0', maxWidth: '500px', textAlign: 'center', marginBottom: '2rem' }}>
          {t('intro.creatingHint')}
        </p>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', marginBottom: '3rem' }}>
          <div style={{ width: '12px', height: '12px', background: '#667eea', borderRadius: '50%', animation: 'bounce 1s infinite' }}></div>
          <div style={{ width: '12px', height: '12px', background: '#667eea', borderRadius: '50%', animation: 'bounce 1s infinite 0.2s' }}></div>
          <div style={{ width: '12px', height: '12px', background: '#667eea', borderRadius: '50%', animation: 'bounce 1s infinite 0.4s' }}></div>
        </div>

        {/* Skip button - now more prominent */}
        <button
          onClick={() => {
            if (onContinue) {
              onContinue();
            } else {
              const playerId = localStorage.getItem(`game_${gameId}_player`);
              const url = playerId ? `/game/${gameId}?playerId=${playerId}` : `/game/${gameId}`;
              window.location.href = url;
            }
          }}
          style={{
            width: isMobile ? '100%' : undefined,
            boxSizing: 'border-box',
            padding: isMobile ? '1rem 1.5rem' : '1.25rem 2.5rem',
            fontSize: isMobile ? '1.05rem' : '1.2rem',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.3s',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 15px 40px rgba(102, 126, 234, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.4)';
          }}
        >
          🎮 {t('intro.enterGame')}
        </button>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        color: '#e2e8f0',
        padding: '2rem',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#fca5a5' }}>
          {t('intro.loadError')}
        </h2>
        <p style={{ color: '#a0aec0', marginBottom: '2rem' }}>{error}</p>
        <button
          onClick={onContinue}
          style={{
            padding: '1rem 2rem',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          {t('intro.continueWithout')}
        </button>
      </div>
    );
  }

  if (!intro) return null;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      color: '#e2e8f0',
      padding: isMobile ? '1rem' : '2rem',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background Image */}
      {intro.imageId && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${apiUrl}/api/media/${intro.imageId})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3,
          filter: 'blur(5px)',
        }}></div>
      )}

      {/* Content */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: '900px',
        width: '100%',
      }}>
        {/* Title */}
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? '2rem' : '3rem',
          animation: 'fadeIn 1s ease-out',
        }}>
          <div style={{ fontSize: isMobile ? '3rem' : '4rem', marginBottom: '1rem' }}>📜</div>
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '3rem',
            marginBottom: '1rem',
            textShadow: '0 4px 20px rgba(0,0,0,0.5)',
            fontWeight: 'bold',
          }}>
            {t('intro.adventureBegins')}
          </h1>
        </div>

        {/* Main Image */}
        {intro.imageId && (
          <div style={{
            marginBottom: isMobile ? '2rem' : '3rem',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            animation: 'fadeIn 1.5s ease-out 0.5s both',
          }}>
            <img
              src={`${apiUrl}/api/media/${intro.imageId}`}
              alt={t('intro.openingAlt')}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </div>
        )}

        {/* Narrative Text */}
        <div style={{
          background: 'rgba(15, 12, 41, 0.9)',
          backdropFilter: 'blur(20px)',
          padding: isMobile ? '1.25rem' : '2.5rem',
          borderRadius: '20px',
          border: '1px solid rgba(99, 102, 241, 0.3)',
          marginBottom: '2rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          animation: 'fadeIn 2s ease-out 1s both',
        }}>
          <p style={{
            fontSize: isMobile ? '1.05rem' : '1.3rem',
            lineHeight: isMobile ? '1.7' : '2',
            textAlign: 'justify',
            color: '#e2e8f0',
            whiteSpace: 'pre-wrap',
          }}>
            {stripMarkup(intro.narrative)}
          </p>
        </div>

        {/* Background music — loops quietly under the narration */}
        {intro.musicId && (
          <audio
            src={`${apiUrl}/api/media/${intro.musicId}`}
            autoPlay
            loop
            ref={(el) => { if (el) el.volume = 0.22; }}
          />
        )}

        {/* Audio Player */}
        {intro.audioId && (
          <div style={{
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'center',
            animation: 'fadeIn 2.5s ease-out 1.5s both',
          }}>
            <div style={{
              background: 'rgba(15, 12, 41, 0.8)',
              borderRadius: '16px',
              padding: isMobile ? '1rem 1.25rem' : '1.5rem 2rem',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              minWidth: isMobile ? 0 : '400px',
              width: isMobile ? '100%' : undefined,
              boxSizing: 'border-box',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '0.75rem',
              }}>
                <span style={{ fontSize: '1.5rem' }}>🎙️</span>
                <span style={{ color: '#cbd5e0', fontWeight: '600' }}>{t('intro.professionalVoice')}</span>
              </div>
              <audio
                controls
                autoPlay
                style={{
                  width: '100%',
                  borderRadius: '8px',
                }}
                src={`${apiUrl}/api/media/${intro.audioId}`}
              >
                {t('intro.audioUnsupported')}
              </audio>
            </div>
          </div>
        )}

        {/* Voice Controls (Fallback to Web Speech) */}
        {!intro.audioId && intro.useWebSpeech && (
          <div style={{
            marginBottom: '2rem',
            textAlign: 'center',
            animation: 'fadeIn 2.5s ease-out 1.5s both',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              alignItems: 'center',
            }}>
              <button
                onClick={() => {
                  speakNarrative(stripMarkup(intro.narrative));
                }}
                disabled={isSpeaking}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  border: 'none',
                  borderRadius: '10px',
                  background: isSpeaking ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.6)',
                  color: '#fff',
                  cursor: isSpeaking ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                🔊 {isSpeaking ? t('intro.speaking') : t('intro.speakAgain')}
              </button>
              <button
                onClick={() => {
                  if ('speechSynthesis' in window) {
                    window.speechSynthesis.cancel();
                  }
                }}
                disabled={!isSpeaking}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  border: 'none',
                  borderRadius: '10px',
                  background: !isSpeaking ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.6)',
                  color: '#fff',
                  cursor: !isSpeaking ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                🔇 {t('intro.stop')}
              </button>
            </div>
            <p style={{
              marginTop: '0.75rem',
              fontSize: '0.85rem',
              color: '#718096',
            }}>
              {t('intro.webSpeechNote')}
            </p>
          </div>
        )}

        {/* Continue Button */}
        <div style={{
          textAlign: 'center',
          animation: 'fadeIn 3s ease-out 2s both',
        }}>
          <button
            onClick={() => {
              if (onContinue) {
                onContinue();
              } else {
                // Get player ID from localStorage
                const playerId = localStorage.getItem(`game_${gameId}_player`);
                const url = playerId ? `/game/${gameId}?playerId=${playerId}` : `/game/${gameId}`;
                window.location.href = url;
              }
            }}
            style={{
              width: isMobile ? '100%' : undefined,
              boxSizing: 'border-box',
              padding: isMobile ? '1.1rem 1.5rem' : '1.5rem 3rem',
              fontSize: isMobile ? '1.1rem' : '1.3rem',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.3s',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
              animation: 'pulse 2s infinite',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.4)';
            }}
          >
            ⚔️ {t('intro.startAdventure')}
          </button>

          {/* Skip Intro Link */}
          <div style={{ marginTop: '1.5rem' }}>
            <button
              onClick={() => {
                if (onContinue) {
                  onContinue();
                } else {
                  const playerId = localStorage.getItem(`game_${gameId}_player`);
                  const url = playerId ? `/game/${gameId}?playerId=${playerId}` : `/game/${gameId}`;
                  window.location.href = url;
                }
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#a0aec0',
                fontSize: '0.95rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#a0aec0';
              }}
            >
              {t('intro.skip')} →
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}
